import _objectWithoutProperties from "/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";
var _excluded = ["height", "width", "redraw", "datasetIdKey", "type", "data", "options", "plugins", "fallbackContent", "updateMode"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { forwardRef, useRef, useEffect } from 'react';
import { Chart as Chart$1, LineController, BarController, RadarController, DoughnutController, PolarAreaController, BubbleController, PieController, ScatterController } from 'chart.js';
var defaultDatasetIdKey = 'label';
function reforwardRef(ref, value) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
}
function setOptions(chart, nextOptions) {
  Object.assign(chart.options, nextOptions);
}
function setLabels(currentData, nextLabels) {
  currentData.labels = nextLabels;
}
function setDatasets(currentData, nextDatasets) {
  var datasetIdKey = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : defaultDatasetIdKey;
  var addedDatasets = [];
  currentData.datasets = nextDatasets.map(function (nextDataset) {
    // given the new set, find it's current match
    var currentDataset = currentData.datasets.find(function (dataset) {
      return dataset[datasetIdKey] === nextDataset[datasetIdKey];
    });
    // There is no original to update, so simply add new one
    if (!currentDataset || !nextDataset.data || addedDatasets.includes(currentDataset)) {
      return _objectSpread({}, nextDataset);
    }
    addedDatasets.push(currentDataset);
    Object.assign(currentDataset, nextDataset);
    return currentDataset;
  });
}
function cloneData(data) {
  var datasetIdKey = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : defaultDatasetIdKey;
  var nextData = {
    labels: [],
    datasets: []
  };
  setLabels(nextData, data.labels);
  setDatasets(nextData, data.datasets, datasetIdKey);
  return nextData;
}
/**
 * Get dataset from mouse click event
 * @param chart - Chart.js instance
 * @param event - Mouse click event
 * @returns Dataset
 */
function getDatasetAtEvent(chart, event) {
  return chart.getElementsAtEventForMode(event.nativeEvent, 'dataset', {
    intersect: true
  }, false);
}
/**
 * Get single dataset element from mouse click event
 * @param chart - Chart.js instance
 * @param event - Mouse click event
 * @returns Dataset
 */
function getElementAtEvent(chart, event) {
  return chart.getElementsAtEventForMode(event.nativeEvent, 'nearest', {
    intersect: true
  }, false);
}
/**
 * Get all dataset elements from mouse click event
 * @param chart - Chart.js instance
 * @param event - Mouse click event
 * @returns Dataset
 */
function getElementsAtEvent(chart, event) {
  return chart.getElementsAtEventForMode(event.nativeEvent, 'index', {
    intersect: true
  }, false);
}
function ChartComponent(param, ref) {
  var _param$height = param.height,
    height = _param$height === void 0 ? 150 : _param$height,
    _param$width = param.width,
    width = _param$width === void 0 ? 300 : _param$width,
    _param$redraw = param.redraw,
    redraw = _param$redraw === void 0 ? false : _param$redraw,
    datasetIdKey = param.datasetIdKey,
    type = param.type,
    data = param.data,
    options = param.options,
    _param$plugins = param.plugins,
    plugins = _param$plugins === void 0 ? [] : _param$plugins,
    fallbackContent = param.fallbackContent,
    updateMode = param.updateMode,
    props = _objectWithoutProperties(param, _excluded);
  var canvasRef = useRef(null);
  var chartRef = useRef();
  var renderChart = function renderChart() {
    if (!canvasRef.current) return;
    chartRef.current = new Chart$1(canvasRef.current, {
      type: type,
      data: cloneData(data, datasetIdKey),
      options: options && _objectSpread({}, options),
      plugins: plugins
    });
    reforwardRef(ref, chartRef.current);
  };
  var destroyChart = function destroyChart() {
    reforwardRef(ref, null);
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
  };
  useEffect(function () {
    if (!redraw && chartRef.current && options) {
      setOptions(chartRef.current, options);
    }
  }, [redraw, options]);
  useEffect(function () {
    if (!redraw && chartRef.current) {
      setLabels(chartRef.current.config.data, data.labels);
    }
  }, [redraw, data.labels]);
  useEffect(function () {
    if (!redraw && chartRef.current && data.datasets) {
      setDatasets(chartRef.current.config.data, data.datasets, datasetIdKey);
    }
  }, [redraw, data.datasets]);
  useEffect(function () {
    if (!chartRef.current) return;
    if (redraw) {
      destroyChart();
      setTimeout(renderChart);
    } else {
      chartRef.current.update(updateMode);
    }
  }, [redraw, options, data.labels, data.datasets, updateMode]);
  useEffect(function () {
    if (!chartRef.current) return;
    destroyChart();
    setTimeout(renderChart);
  }, [type]);
  useEffect(function () {
    renderChart();
    return function () {
      return destroyChart();
    };
  }, []);
  return /*#__PURE__*/React.createElement("canvas", Object.assign({
    ref: canvasRef,
    role: "img",
    height: height,
    width: width
  }, props), fallbackContent);
}
var Chart = /*#__PURE__*/forwardRef(ChartComponent);
function createTypedChart(type, registerables) {
  Chart$1.register(registerables);
  return /*#__PURE__*/forwardRef(function (props, ref) {
    return /*#__PURE__*/React.createElement(Chart, Object.assign({}, props, {
      ref: ref,
      type: type
    }));
  });
}
var Line = /* #__PURE__ */createTypedChart('line', LineController);
var Bar = /* #__PURE__ */createTypedChart('bar', BarController);
var Radar = /* #__PURE__ */createTypedChart('radar', RadarController);
var Doughnut = /* #__PURE__ */createTypedChart('doughnut', DoughnutController);
var PolarArea = /* #__PURE__ */createTypedChart('polarArea', PolarAreaController);
var Bubble = /* #__PURE__ */createTypedChart('bubble', BubbleController);
var Pie = /* #__PURE__ */createTypedChart('pie', PieController);
var Scatter = /* #__PURE__ */createTypedChart('scatter', ScatterController);
export { Bar, Bubble, Chart, Doughnut, Line, Pie, PolarArea, Radar, Scatter, getDatasetAtEvent, getElementAtEvent, getElementsAtEvent };